import type { AudioBackend } from './engine';

const BELL_URL = '/audio/bell.m4a';

/** One second of silence as a WAV blob, looped to keep the iOS audio session alive. */
function silentWavUrl(): string {
  const sampleRate = 8000;
  const samples = sampleRate; // 1 second
  const buffer = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buffer);
  const write = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  write(0, 'RIFF');
  v.setUint32(4, 36 + samples * 2, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  write(36, 'data');
  v.setUint32(40, samples * 2, true);
  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

export function createWebAudioBackend(): AudioBackend {
  let ctx: AudioContext | null = null;
  let bellBuffer: AudioBuffer | null = null;
  let bellLoading: Promise<void> | null = null;
  let musicEl: HTMLAudioElement | null = null;
  let musicGain: GainNode | null = null;
  let silentEl: HTMLAudioElement | null = null;
  let endedCb: (() => void) | null = null;
  let musicGen = 0; // bumps on every start/stop so a late fade-out never pauses the next track

  /** One element and one Web Audio source for the whole app session, reused for every track. */
  const ensureMusic = () => {
    if (musicEl && musicGain) return;
    if (!ctx) return;
    const el = new Audio();
    el.preload = 'auto';
    el.addEventListener('ended', () => endedCb?.());
    const node = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    node.connect(gain);
    gain.connect(ctx.destination);
    musicEl = el;
    musicGain = gain;
  };

  const loadBell = () => {
    if (bellLoading || !ctx) return bellLoading ?? Promise.resolve();
    const c = ctx;
    bellLoading = fetch(BELL_URL)
      .then(r => r.arrayBuffer())
      .then(buf => c.decodeAudioData(buf))
      .then(decoded => {
        bellBuffer = decoded;
      })
      .catch(() => {
        bellLoading = null; // allow a retry on the next bell
      });
    return bellLoading;
  };

  const resumeIfNeeded = () => {
    if (ctx && ctx.state !== 'running') ctx.resume().catch(() => undefined);
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') resumeIfNeeded();
  };

  return {
    now() {
      return ctx ? ctx.currentTime : performance.now() / 1000;
    },

    unlock() {
      // Everything here must run synchronously inside the tap.
      const nav = navigator as Navigator & { audioSession?: { type: string } };
      if (nav.audioSession) nav.audioSession.type = 'playback';
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!ctx && AC) ctx = new AC();
      resumeIfNeeded();
      void loadBell();
      ensureMusic();
      document.removeEventListener('visibilitychange', onVisible);
      document.addEventListener('visibilitychange', onVisible);
    },

    scheduleBell(at, volume) {
      let cancelled = false;
      let source: AudioBufferSourceNode | null = null;
      const play = () => {
        if (cancelled || !ctx || !bellBuffer) return;
        resumeIfNeeded();
        source = ctx.createBufferSource();
        const gain = ctx.createGain();
        gain.gain.value = volume;
        source.buffer = bellBuffer;
        source.connect(gain);
        gain.connect(ctx.destination);
        source.start(Math.max(at, ctx.currentTime));
      };
      if (bellBuffer) play();
      else void loadBell().then(play);
      return () => {
        cancelled = true;
        try {
          source?.stop();
        } catch {
          /* already stopped */
        }
      };
    },

    startMusic({ src, loop, volume, fadeInSec, seekTo }) {
      if (!ctx) return;
      ensureMusic();
      if (!musicEl || !musicGain) return;
      musicGen++;
      const el = musicEl;
      el.pause();
      el.loop = loop;
      el.src = src;
      el.currentTime = 0;
      musicGain.gain.cancelScheduledValues(ctx.currentTime);
      musicGain.gain.setValueAtTime(0, ctx.currentTime);
      musicGain.gain.linearRampToValueAtTime(volume, ctx.currentTime + Math.max(0.05, fadeInSec));
      if (seekTo) el.currentTime = seekTo;
      el.play().catch(() => undefined);
    },

    pauseMusic() {
      musicEl?.pause();
    },

    resumeMusic() {
      resumeIfNeeded();
      musicEl?.play().catch(() => undefined);
    },

    seekMusic(seconds) {
      if (musicEl) musicEl.currentTime = seconds;
    },

    setMusicVolume(volume) {
      if (!ctx || !musicGain) return;
      musicGain.gain.cancelScheduledValues(ctx.currentTime);
      musicGain.gain.setTargetAtTime(volume, ctx.currentTime, 0.05);
    },

    fadeOutMusic(seconds) {
      if (!ctx || !musicGain || !musicEl) return;
      const el = musicEl;
      musicGain.gain.cancelScheduledValues(ctx.currentTime);
      musicGain.gain.setValueAtTime(musicGain.gain.value, ctx.currentTime);
      musicGain.gain.linearRampToValueAtTime(0, ctx.currentTime + seconds);
      const gen = musicGen;
      window.setTimeout(() => {
        if (gen === musicGen) el.pause();
      }, seconds * 1000 + 100);
    },

    stopMusic() {
      // Keep the element: it is already allowed to play, a new one would need another tap on iOS.
      musicGen++;
      musicEl?.pause();
      if (ctx && musicGain) {
        musicGain.gain.cancelScheduledValues(ctx.currentTime);
        musicGain.gain.setValueAtTime(0, ctx.currentTime);
      }
    },

    keepAlive(on) {
      if (on) {
        if (!silentEl) {
          silentEl = new Audio(silentWavUrl());
          silentEl.loop = true;
        }
        silentEl.play().catch(() => undefined);
      } else {
        silentEl?.pause();
      }
    },

    onMusicEnded(cb) {
      endedCb = cb;
    },
  };
}
